<script setup lang="ts">
import { computed, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import AdminBuildInfo from '@/components/admin/AdminBuildInfo.vue'
import AdminCalendars from '@/components/admin/AdminCalendars.vue'
import AdminDatabase from '@/components/admin/AdminDatabase.vue'
import AdminMcpTokens from '@/components/admin/AdminMcpTokens.vue'
import AdminSettings from '@/components/admin/AdminSettings.vue'
import NavList, { type NavGroup } from '@/components/NavList.vue'
import PageHeader from '@/components/PageHeader.vue'
import { useBackButton } from '@/composables/useBackButton'
import { goBack } from '@/router/goBack'
import { ADMIN_SECTIONS, findAdminSection } from '@/admin/sections'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

// `/admin` lists the subpages, `/admin/<key>` shows one of them.
const current = computed(() => findAdminSection(route.params.section))
watchEffect(() => {
  if (route.params.section && !current.value) void router.replace('/admin')
})
useBackButton(() => goBack(router, current.value ? '/admin' : '/todos'))

function sectionItems(group: 'server' | 'data') {
  return ADMIN_SECTIONS.filter((section) => section.group === group).map((section) => ({
    to: `/admin/${section.key}`,
    icon: section.icon,
    title: t(section.titleKey),
    hint: t(section.hintKey),
  }))
}
const groups = computed<NavGroup[]>(() => [
  { key: 'server', items: sectionItems('server') },
  { key: 'data', items: sectionItems('data') },
  { key: 'bot', items: [{ to: '/admin/bot', icon: '🤖', title: t('admin.bot.link'), hint: t('admin.sections.botHint') }] },
])
</script>

<template>
  <div>
    <PageHeader v-if="!current" :title="t('nav.admin')" :hint="t('admin.indexHint')" />
    <NavList v-if="!current" :groups="groups" />

    <template v-else>
      <PageHeader :title="t(current.titleKey)" back-to="/admin" :back-label="t('admin.back')" />
      <AdminBuildInfo v-if="current.key === 'build'" />
      <AdminSettings v-else-if="current.key === 'settings'" />
      <AdminDatabase v-else-if="current.key === 'database'" />
      <AdminCalendars v-else-if="current.key === 'calendars'" />
      <AdminMcpTokens v-else-if="current.key === 'tokens'" />
    </template>
  </div>
</template>
